import React, { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/styles'
import * as R from 'ramda'
import {
  Typography,
  CircularProgress,
  Divider,
  useTheme,
  useMediaQuery,
} from '@material-ui/core'
import Assets from 'app/configs/assetConfigs'
import { FormattedMessage, useIntl } from 'react-intl'
import effects from 'app/effects/effects'
import CommonBackground from 'app/components/CommonBackground'
import ContainerNotifyMeCard from 'app/components/ContainerNotifyMeCard'
import AuthenticateCardFooter from 'app/components/AuthenticateCardFooter'
import { useBreakpoints } from 'commons/useBreakpoints'
import { useFlag } from '@unleash/proxy-client-react'
import ConfirmPasswordSuccess from '../resetPassword/ConfirmPasswordSuccess'
import ConfirmPasswordError from '../resetPassword/ConfirmPasswordError'
import MessagespringLoading from 'app/componentV4/Loader/MessagespringLoading'

const useStyles = makeStyles((theme) => ({
  root: {
    color: theme.palette.primary.Typography,
    'background-color': theme.palette.register.background,
  },
  registerHero: {
    'background-color': theme.palette.register.darkBackground,
    '& p': {
      color: theme.palette.register.contrastText,
    },
  },
  logoImage: {
    height: '120px',
  },
  errorText: {
    color: theme.palette.negative.main,
  },
  darkGreyBlueText: {
    color: theme.palette.darkGreyBlue.main,
  },
  responsiveSize: {
    [useBreakpoints(theme).lessThan.md]: {
      maxWidth: '290px',
    },
  },
}))

export default (props) => {
  const mes_3908_place_profile = useFlag('mes_3908_place_profile')

  const { formatMessage } = useIntl()
  const LOADING = 0
  const SUCCESS = 1
  const FAILURE = 2
  const classes = useStyles()
  const [displayState, setDisplayState] = useState(LOADING)
  const getToken = () => {
    return props.match.params.token
  }
  const theme = useTheme()
  const isDownXs = useMediaQuery(theme.breakpoints.down('sm'))

  const apiFetchersCallback = useCallback(
    ({ token }) => {
      return mes_3908_place_profile
        ? effects.apis.profileInfo.confirmNewPlaceEmail({ token })
        : effects.apis.confirmChangingPlaceEmail({ token })
    },
    [mes_3908_place_profile],
  )

  const token = getToken()
  useEffect(() => {
    if (token === null) {
      setDisplayState(FAILURE)
      return
    }
    apiFetchersCallback({ token })
      .then(() => {
        setDisplayState(SUCCESS)
      })
      .catch((error) => {
        setDisplayState(FAILURE)
      })
  }, [apiFetchersCallback, token])

  const getErrorSection = () => {
    return (
      <div className="flex flex-col">
        <img
          alt="App Logo"
          src={Assets.redMailWithCircleBgIcon}
          className={clsx(classes.logoImage, 'p-12', 'h-128')}
        />
        <Typography
          variant="h5"
          className={clsx(classes.errorText, 'py-24 font-semibold text-center')}
        >
          <FormattedMessage
            id="verifyingEmailOnPlaceChanging.failure.title"
            values={{ br: isDownXs ? <br /> : '' }}
          ></FormattedMessage>
        </Typography>
        <Divider
          className={clsx(
            classes.responsiveSize,
            'flex self-center mx-16 mt-8 mb-32 w-11/12 xs:w-11/12 sm:w-11/12 md:w-1/2 md:hidden lg:hidden xl:hidden',
          )}
        />
        <div className={'flex px-36 pb-24 justify-center'}>
          <Typography
            variant="h6"
            className={clsx(
              classes.responsiveSize,
              classes.darkGreyBlueText,
              'font-medium text-center',
            )}
          >
            <FormattedMessage id="verifyingEmailOnPlaceChanging.failure.body"></FormattedMessage>
          </Typography>
        </div>
        <div className="w-full items-center px-8">
          <AuthenticateCardFooter />
        </div>
      </div>
    )
  }

  const getProgressSection = () => {
    return (
      <div className="flex flex-col pt-56 items-center">
        <CircularProgress className="mx-auto mb-32" color="secondary" />
        <Typography
          variant="h5"
          gutterBottom
          className={clsx(
            classes.responsiveSize,
            classes.darkGreyBlueText,
            'py-24 px-24 text-center font-medium',
          )}
        >
          <FormattedMessage id="verifyingEmailOnPlaceChanging.progress.title"></FormattedMessage>
        </Typography>
      </div>
    )
  }

  const getProgressSectionV4 = () => {
    return (
      <div className="flex justify-center pt-56 items-center h-full">
        <MessagespringLoading />
      </div>
    )
  }

  const getSuccessSection = () => {
    return (
      <div className="flex flex-col">
        <img
          alt="App Logo"
          src={Assets.registerSuccess.headerImage}
          className={clsx(classes.logoImage, 'p-12', 'h-128')}
        />
        <Typography
          variant="h5"
          color="secondary"
          className="pt-24 text-center font-semibold mb-32"
        >
          <FormattedMessage id="verifyingEmailOnPlaceChanging.success.title"></FormattedMessage>
        </Typography>
        <Divider
          className={clsx(
            classes.responsiveSize,
            'flex self-center mx-16 mt-8 mb-32 w-11/12 xs:w-11/12 sm:w-11/12 md:w-1/2 md:hidden lg:hidden xl:hidden',
          )}
        />
        <div className="block px-36 m-auto">
          {R.compose(
            R.map((val) => {
              return (
                <Typography
                  key={val}
                  variant="h6"
                  gutterBottom
                  className={clsx(
                    classes.responsiveSize,
                    classes.darkGreyBlueText,
                    'text-center',
                    'mb-0',
                  )}
                >
                  {val}
                </Typography>
              )
            }),
            R.split('\n'),
          )(
            formatMessage({ id: 'verifyingEmailOnPlaceChanging.success.body' }),
          )}
        </div>
      </div>
    )
  }

  const display = () => {
    switch (displayState) {
      case LOADING:
        return getProgressSection()
      case SUCCESS:
        return getSuccessSection()
      default:
        return getErrorSection()
    }
  }

  const displayV4 = () => {
    switch (displayState) {
      case LOADING:
        return getProgressSectionV4()
      case SUCCESS:
        return <ConfirmPasswordSuccess />
      default:
        return <ConfirmPasswordError />
    }
  }

  return mes_3908_place_profile ? (
    displayV4()
  ) : (
    <CommonBackground>
      <ContainerNotifyMeCard>
        <div className="flex flex-col py-36 px-8">{display()}</div>
      </ContainerNotifyMeCard>
    </CommonBackground>
  )
}
